import React, { useState } from 'react'
import debounce from 'lodash/debounce'
import { uuid } from 'uuidv4'
import axios from 'axios'
import {
  Button,
  Grid,
  Divider,
  InputBase,
  CardActions,
  CardContent,
  CardHeader,
  Card,
  Backdrop
} from '@material-ui/core'
import { ChevronRight as ChevronRightIcon } from '@material-ui/icons'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Link } from 'react-router-dom'
import { massProcessingApi } from '@atheneum/api'
import SearchAndCreateList from './components/SearchAndCreateList'
import RemoveList from './components/RemoveList'
import getAdditionalEmployee from './api/getAdditionalEmployee'

import Header from './components/Header'
import SharedWith from './components/SharedWith'

import ListView from './ListView'
import './massProcessing.sass'
import AddIcon from '@material-ui/icons/Add'

function reducer(state, action) {
  switch (action.type) {
    case 'LOAD_LISTS':
      return { ...state, loading: true }
    case 'LOAD_LISTS_DONE':
      return {
        ...state,
        loading: false,
        lists: action.payload
      }
    case 'SET_LISTS_STATIC':
      return { ...state, staticLists: action.payload }
    case 'SET_ALL_EMPLOYEES':
      return { ...state, allEmployees: action.payload }
    case 'LOAD_NUMEXPERTS_DONE':
      return { ...state, loading: false }
    case 'ADD_EMPLOYEE':
      const employeeToAdd = action.payload.employee
      const newList = state.lists.map(list => {
        if (list.id !== action.payload.id) {
          return list
        }
        return { ...list, sharedWith: [...list.sharedWith, employeeToAdd] }
      })
      return { ...state, lists: newList }
    case 'REMOVE_EMPLOYEE':
      const employeeToRemove = action.payload.userId
      const updatedList = state.lists.map(list => {
        if (list.id !== action.payload.id) {
          return list
        }

        return {
          ...list,
          sharedWith: list.sharedWith.filter(l => l.userId !== employeeToRemove)
        }
      })
      return { ...state, lists: updatedList }
    default:
      return state
  }
}

const initialState = {
  loading: false,
  error: null,
  lists: [],
  allEmployees: []
}

const DispatchContext = React.createContext()
const StateContext = React.createContext()

const DEBOUNCE_TIME = 500

const setLists = async dispatch => {
  dispatch({ type: 'LOAD_LISTS' })

  const getAllLists = await massProcessingApi.getLists()
  const uniqueOwners = [...new Set(getAllLists.map(l => l.owner))]
  const mapOwners = uniqueOwners.map(async o => await getAdditionalEmployee(o))
  const resolveOwnerObj = await Promise.all(mapOwners)
  const ownerObj = resolveOwnerObj.reduce((acc, cur) => {
    return { ...acc, [cur.data.id]: cur }
  }, {})

  const lists = getAllLists.map(list => {
    return {
      ...list,
      ownerName: ownerObj[list.owner].data.fullName,
      sharedWith: [
        {
          currentPosition: {},
          email: ownerObj[list.owner].data.email,
          firstName: ownerObj[list.owner].data.firstName,
          fullName: ownerObj[list.owner].data.fullName,
          lastName: ownerObj[list.owner].data.lastName,
          picture: !!ownerObj[list.owner].data.picture?.path
            ? ownerObj[list.owner].data.picture.path
            : '',
          userId: ownerObj[list.owner].data.id
        },
        ...list.sharedWith
      ]
    }
  })

  dispatch({ type: 'SET_LISTS_STATIC', payload: lists })
  dispatch({ type: 'LOAD_LISTS_DONE', payload: lists })
}

export const MassProcessing = () => {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  React.useEffect(() => {
    axios({
      method: 'get',
      url: '/api/employee/list-all',
      headers: {
        authorization: localStorage.getItem('ngStorage-accessToken')
      }
    }).then(res => {
      const activeEmployees = res.data.filter(employee => employee.active)
      dispatch({ type: 'SET_ALL_EMPLOYEES', payload: activeEmployees })
    })
    ;(async () => {
      await setLists(dispatch)
    })()
  }, [])
  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>
        <div className="mp-container-wrapper">
          <Header />
          <SearchAndCreateList dispatch={dispatch} lists={state.staticLists} />
          <ExpertLists />
        </div>
      </StateContext.Provider>
    </DispatchContext.Provider>
  )
}

const ExpertLists = () => {
  const state = React.useContext(StateContext)
  const dispatch = React.useContext(DispatchContext)
  const { lists, loading, error } = state

  if (loading)
    return (
      <Backdrop
        style={{
          zIndex: 999
        }}
        open={true}>
        <CircularProgress style={{ color: '#429bdc' }} />
      </Backdrop>
    )

  if (error) return <div>error, {JSON.stringify(error)}</div>

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item>
          <Card
            style={{
              position: 'relative',
              padding: '0',
              boxShadow:
                '0px 2px 2px 1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
              overflow: 'unset',
              background: '#4d9be1bd',
              color: '#fff',
              cursor: 'pointer'
            }}
            className="mp-listCard al-hover-add-list"
            onClick={async () => {
              await massProcessingApi.createList({ name: 'My List', type: 'expert' })
              dispatch({ type: 'LOAD_LISTS' })
              const getAllLists = await massProcessingApi.getLists()
              const lists = getAllLists.map(async list => {
                const owner = await getAdditionalEmployee(list.owner)
                return {
                  ...list,
                  ownerName: owner.data.fullName,
                  sharedWith: [
                    {
                      currentPosition: {},
                      email: owner.data.email,
                      firstName: owner.data.firstName,
                      fullName: owner.data.fullName,
                      lastName: owner.data.lastName,
                      picture: !!owner.data.picture?.path
                        ? owner.data.picture.path
                        : '',
                      userId: owner.data.id
                    },
                    ...list.sharedWith
                  ]
                }
              })

              const resolveLists = await Promise.all(lists)
              const uniqueSharedWith = resolveLists.map(r => {
                return {
                  ...r,
                  sharedWith: Array.from(
                    new Set(r.sharedWith.map(a => a.userId))
                  ).map(id => {
                    return r.sharedWith.find(a => a.userId === id)
                  })
                }
              })
              dispatch({ type: 'SET_LISTS_STATIC', payload: uniqueSharedWith })
              dispatch({ type: 'LOAD_LISTS_DONE', payload: uniqueSharedWith })
            }}>
            <CardContent
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '199px',
                justifyContent: 'center',
                paddingTop: '33px'
              }}>
              <div>
                <AddIcon style={{ fontSize: '48px' }} />
              </div>
              <p>Create List</p>
            </CardContent>
          </Card>
        </Grid>
        {lists.map(list => {
          return (
            <Grid item key={uuid()}>
              <ListItem item={list} />
            </Grid>
          )
        })}
      </Grid>
    </div>
  )
}

const ListItem = ({ item }) => {
  const dispatch = React.useContext(DispatchContext)
  const state = React.useContext(StateContext)
  const [name, setName] = React.useState(item.name)
  const onNameChange = debounce(async name => {
    massProcessingApi.updateList({ id: item.id, data: { name } })

    setName(name)

    const updatedList = state.staticLists.map(list =>
      list.id !== item.id ? list : { ...list, name }
    )
    dispatch({ type: 'SET_LISTS_STATIC', payload: updatedList })
    dispatch({ type: 'LOAD_LISTS_DONE', payload: updatedList })
  }, DEBOUNCE_TIME)

  const [openRemoveList, setOpenRemoveList] = useState(false)

  const handleOpenRemoveList = () => {
    setOpenRemoveList(true)
  }

  const handleCloseRemoveList = async () => {
    await setLists(dispatch)
    setOpenRemoveList(false)
    return
  }

  return (
    <Card
      style={{
        position: 'relative',
        padding: '0',
        boxShadow:
          '0px 2px 2px 1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
        overflow: 'unset'
      }}
      className="mp-listCard">
      <CardHeader
        style={{ position: 'relative' }}
        action={
          <RemoveList
            item={item}
            page="massProcessing"
            openRemoveList={openRemoveList}
            handleOpenRemoveList={handleOpenRemoveList}
            handleCloseRemoveList={handleCloseRemoveList}
          />
        }
      />

      <CardContent style={{ padding: '0 16px', position: 'relative' }}>
        <InputBase
          defaultValue={name}
          placeholder="My List"
          inputProps={
            ({ 'aria-label': 'naked' },
            {
              style: {
                font: 'normal normal bold 16px/22px Roboto',
                letterSpacing: '0.12px',
                color: '#474B4F',
                padding: '0'
              }
            })
          }
          onChange={e => onNameChange(e.target.value)}
        />
        <p
          style={{
            font: 'normal normal normal 14px/16px Roboto',
            letterSpacing: '0.1px',
            color: '#535353'
          }}>
          Owner: <span className="al_list_owner_qa">{item.ownerName}</span>{' '}
        </p>
        <Divider />
        <h4
          style={{
            font: 'normal normal normal 14px/16px Roboto',
            letterSpacing: '0.1px',
            color: '#535353'
          }}>
          {' '}
          Experts: <span className="al_expert_count_qa">{item.noItems}</span>
        </h4>
      </CardContent>
      <CardActions className="mp-listActions" disableSpacing>
        <Link to={'/list/' + item.id}>
          <Button
            onClick={() =>
              localStorage.setItem(
                'lastMassProcessingList',
                JSON.stringify(item)
              )
            }
            style={{ color: '#429bdc' }}>
            <span
              style={{
                font: 'normal normal normal 14px/16px Roboto',
                letterSpacing: '0.1px',
                color: '#429ADC'
              }}></span>
            <ChevronRightIcon style={{ color: '#429bdc', fontSize: '40px' }} />
          </Button>
        </Link>
      </CardActions>

      <SharedWith
        allEmployees={state.allEmployees}
        employees={item.sharedWith}
        item={item}
        dispatch={dispatch}
      />
    </Card>
  )
}

export const MassProcessingList = props => {
  const id = props.match.params.id
  return <ListView id={id} history={props.history} />
}
